import * as React from 'react';
import { useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import { colors, shadows } from '../features/employees/styles/personalakteStyles';

export interface ShiftApplication {
  id: string | number;
  status: 'APPLIED' | 'APPROVED' | 'REJECTED' | 'PENDING';
  employee_name?: string;
  employee: string | number;
  employee_photo?: string;
  type: string;
}

export interface AssignedEmployee {
  id: number;
  first_name: string;
  last_name: string;
}

export interface Shift {
  id: number;
  applications?: ShiftApplication[];
  assigned_employees?: AssignedEmployee[];
}

export interface EmployeeWithPhoto {
  id: number;
  first_name: string;
  last_name: string;
  photo_url?: string;
}

export interface EmployeeWeekStat {
  assignedHours: number;
  targetHours: number;
  appCount: number;
  weekendAppCount: number;
}

export interface ShiftApplicationIndicatorProps {
  shift: Shift;
  onAssignClick?: (shiftId: number, employeeId: number) => void;
  accentColor?: string;
  allEmployees?: EmployeeWithPhoto[];
  employeeStats?: Record<number, EmployeeWeekStat>;
}

const TOLERANCE_HOURS = 3;

export default function ShiftApplicationIndicator({ shift, onAssignClick, accentColor = '#7c3aed', allEmployees = [], employeeStats = {} }: ShiftApplicationIndicatorProps): React.ReactElement | null {
  const [showPopover, setShowPopover] = useState<boolean>(false);
  const [popoverPosition, setPopoverPosition] = useState<{ top: number; left: number }>({ top: 0, left: 0 });
  const [isHovered, setIsHovered] = useState<boolean>(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const allApps: ShiftApplication[] = shift.applications || [];
  const assigned: AssignedEmployee[] = shift.assigned_employees || [];
  const assignedIds = new Set(assigned.map(emp => emp.id));

  if (allApps.length === 0) {
    return null;
  }

  const hexToRgb = (hex: string) => {
    const cleanHex = hex.replace('#', '');
    return {
      r: parseInt(cleanHex.substring(0, 2), 16),
      g: parseInt(cleanHex.substring(2, 4), 16),
      b: parseInt(cleanHex.substring(4, 6), 16)
    };
  };

  const rgb = hexToRgb(accentColor);

  const handleClick = (e: React.MouseEvent): void => {
    e.stopPropagation();
    if (containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      const popoverHeight = Math.min((allApps.length + assigned.length) * 48 + 60, 400);
      const popoverWidth = 240;
      let top = rect.bottom + 8;
      let left = rect.right - popoverWidth;

      if (top + popoverHeight > window.innerHeight - 16) {
        top = rect.top - popoverHeight - 8;
      }
      if (top < 16) {
        top = 16;
      }
      if (left < 16) {
        left = 16;
      }
      if (left + popoverWidth > window.innerWidth - 16) {
        left = window.innerWidth - popoverWidth - 16;
      }

      setPopoverPosition({ top, left });
    }
    setShowPopover(true);
  };

  const handleNameClick = (e: React.MouseEvent, employeeId: number) => {
    e.stopPropagation();
    if (onAssignClick) {
      onAssignClick(shift.id, employeeId);
      setShowPopover(false);
    }
  };

  const getInitials = (name: string): string => {
    const parts = name.split(' ');
    if (parts.length >= 2) {
      return (parts[0][0] + parts[1][0]).toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  const renderEmployeeMeta = (employeeId: number): React.ReactElement | null => {
    const stat = employeeStats[employeeId];
    if (!stat) {
      return null;
    }

    const assignedHours = Number(stat.assignedHours) || 0;
    const targetHours = Number(stat.targetHours) || 0;
    const appCount = Number(stat.appCount) || 0;
    const weekendAppCount = Number(stat.weekendAppCount) || 0;
    const fillPercent = targetHours > 0 ? Math.min(100, (assignedHours / targetHours) * 100) : 0;
    const hasNothing = assignedHours <= 0;
    let barColor = '#10b981';
    if (assignedHours > targetHours + TOLERANCE_HOURS) {
      barColor = '#ef4444';
    } else if (assignedHours > targetHours) {
      barColor = '#f59e0b';
    } else if (hasNothing) {
      barColor = '#cbd5e1';
    }

    const hasNoApps = appCount === 0;
    const noWeekend = weekendAppCount === 0;
    const counterColor = hasNoApps ? '#991b1b' : noWeekend ? '#92400e' : '#065f46';
    const counterBg = hasNoApps ? 'rgba(239, 68, 68, 0.2)' : noWeekend ? 'rgba(245, 158, 11, 0.2)' : 'rgba(16, 185, 129, 0.2)';

    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginTop: '3px' }}>
        <div
          style={{
            flex: 1,
            minWidth: 0,
            height: '4px',
            background: 'rgba(0, 0, 0, 0.08)',
            borderRadius: '2px',
            overflow: 'hidden',
          }}
        >
          <div
            style={{
              width: `${fillPercent}%`,
              height: '100%',
              background: barColor,
              borderRadius: '2px',
              transition: 'width 0.3s ease',
            }}
          />
        </div>
        <span
          style={{
            fontSize: '9px',
            fontWeight: 600,
            color: hasNothing ? '#94a3b8' : '#64748b',
            whiteSpace: 'nowrap',
          }}
        >
          {assignedHours.toFixed(0)}/{targetHours.toFixed(0)}h
        </span>
        <span
          title={`${appCount} application${appCount === 1 ? '' : 's'} total · ${weekendAppCount} on weekends (Fri eve, Sat, Sun)`}
          style={{
            fontSize: '9px',
            fontWeight: 700,
            whiteSpace: 'nowrap',
            padding: '1px 5px',
            borderRadius: '4px',
            color: counterColor,
            background: counterBg,
          }}
        >
          {appCount}/{weekendAppCount}
        </span>
      </div>
    );
  };

  return (
    <div
      ref={containerRef}
      style={{
        position: 'absolute',
        bottom: 4,
        right: 5,
      }}
    >
      <div
        data-testid="shift-application-indicator"
        onClick={handleClick}
        onMouseDown={(e) => e.stopPropagation()}
        onTouchStart={(e) => e.stopPropagation()}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        style={{
          width: 16,
          height: 16,
          borderRadius: '50%',
          background: `linear-gradient(135deg, ${accentColor} 0%, rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0.8) 100%)`,
          color: '#fff',
          fontSize: 8,
          fontWeight: 700,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
          boxShadow: `0 2px 6px rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0.4)`,
          transition: 'transform 0.15s ease',
          transform: isHovered ? 'scale(1.15)' : 'scale(1)',
        }}
        title={`${allApps.length} application${allApps.length === 1 ? '' : 's'}`}
      >
        {allApps.length}
      </div>

      {showPopover && createPortal(
        <>
          <div
            style={{ position: 'fixed', inset: 0, zIndex: 9998 }}
            onClick={(e) => { e.stopPropagation(); setShowPopover(false); }}
          />
          <div
            data-testid="shift-application-popover"
            style={{
              position: 'fixed',
              top: popoverPosition.top,
              left: popoverPosition.left,
              minWidth: 240,
              maxHeight: 'calc(100vh - 32px)',
              overflowY: 'auto',
              background: 'rgba(255, 255, 255, 0.98)',
              backdropFilter: 'blur(20px)',
              WebkitBackdropFilter: 'blur(20px)',
              borderRadius: '14px',
              padding: '14px 16px',
              border: `1px solid ${colors.border}`,
              boxShadow: shadows.elevated,
              zIndex: 9999,
              animation: 'slideIn 0.15s ease-out',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Assigned Section */}
            {assigned.length > 0 && (
              <div style={{ marginBottom: allApps.length > 0 ? '14px' : 0 }}>
                <div style={{
                  fontSize: '10px',
                  fontWeight: 700,
                  color: '#15803d',
                  marginBottom: '8px',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                }}>
                  Assigned
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  {assigned.map(emp => {
                    const empData = allEmployees.find(e => e.id === emp.id);
                    const name = `${emp.first_name} ${emp.last_name}`;
                    const photoUrl = empData?.photo_url;
                    const hasPhoto = !!photoUrl;
                    return (
                      <div
                        key={emp.id}
                        onClick={onAssignClick ? (e) => handleNameClick(e, emp.id) : undefined}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                          padding: '8px 10px',
                          borderRadius: '8px',
                          cursor: onAssignClick ? 'pointer' : 'default',
                          transition: 'background 0.15s',
                          background: 'rgba(34, 197, 94, 0.06)',
                        }}
                        onMouseEnter={(e) => {
                          if (onAssignClick) e.currentTarget.style.background = 'rgba(239, 68, 68, 0.06)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = 'rgba(34, 197, 94, 0.06)';
                        }}
                      >
                        <div
                          style={{
                            width: '28px',
                            height: '28px',
                            borderRadius: hasPhoto ? '50%' : '6px',
                            background: hasPhoto ? 'transparent' : colors.slate,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: 'white',
                            fontWeight: 700,
                            fontSize: '10px',
                            flexShrink: 0,
                            border: '2px solid #22c55e',
                            overflow: 'hidden',
                          }}
                        >
                          {hasPhoto ? (
                            <img src={photoUrl} alt={name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                          ) : (
                            getInitials(name)
                          )}
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontWeight: 600, fontSize: '12px', color: colors.textPrimary }}>{name}</div>
                          {renderEmployeeMeta(emp.id)}
                        </div>
                        {onAssignClick && (
                          <div
                            style={{
                              fontSize: '10px',
                              color: colors.danger,
                              fontWeight: 600,
                              padding: '3px 8px',
                              background: 'rgba(239, 68, 68, 0.08)',
                              borderRadius: '4px',
                            }}
                          >
                            – remove
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Applications Section */}
            {allApps.length > 0 && (
              <div>
                <div style={{
                  fontSize: '10px',
                  fontWeight: 700,
                  color: colors.slate,
                  marginBottom: '8px',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                }}>
                  Applications
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  {allApps.map(app => {
                    const empId = Number(app.employee);
                    const isAlreadyAssigned = assignedIds.has(empId);
                    const empData = allEmployees.find(e => e.id === empId);
                    const name = app.employee_name || (empData ? `${empData.first_name} ${empData.last_name}` : `Employee #${app.employee}`);
                    const photoUrl = app.employee_photo || empData?.photo_url;
                    const hasPhoto = !!photoUrl;
                    const canAssign = !isAlreadyAssigned && onAssignClick;
                    return (
                      <div
                        key={app.id}
                        onClick={canAssign ? (e) => handleNameClick(e, empId) : undefined}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                          padding: '8px 10px',
                          borderRadius: '8px',
                          cursor: canAssign ? 'pointer' : 'default',
                          transition: 'background 0.15s',
                          background: isAlreadyAssigned ? 'rgba(34, 197, 94, 0.06)' : 'transparent',
                        }}
                        onMouseEnter={(e) => {
                          if (canAssign) e.currentTarget.style.background = colors.bgHover;
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = isAlreadyAssigned ? 'rgba(34, 197, 94, 0.06)' : 'transparent';
                        }}
                      >
                        <div
                          style={{
                            width: '28px',
                            height: '28px',
                            borderRadius: hasPhoto ? '50%' : '6px',
                            background: hasPhoto ? 'transparent' : colors.slate,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: 'white',
                            fontWeight: 700,
                            fontSize: '10px',
                            flexShrink: 0,
                            border: isAlreadyAssigned ? '2px solid #22c55e' : '2px solid rgba(82, 113, 165, 0.3)',
                            overflow: 'hidden',
                          }}
                        >
                          {hasPhoto ? (
                            <img src={photoUrl} alt={name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                          ) : (
                            getInitials(name)
                          )}
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontWeight: 600, fontSize: '12px', color: colors.textPrimary }}>{name}</div>
                          {renderEmployeeMeta(empId)}
                        </div>
                        {isAlreadyAssigned ? (
                          <div
                            style={{
                              fontSize: '10px',
                              color: '#15803d',
                              fontWeight: 600,
                              padding: '3px 8px',
                              background: 'rgba(34, 197, 94, 0.12)',
                              borderRadius: '4px',
                            }}
                          >
                            assigned
                          </div>
                        ) : (
                          <div
                            style={{
                              fontSize: '10px',
                              color: colors.slate,
                              fontWeight: 600,
                              padding: '3px 8px',
                              background: 'rgba(82, 113, 165, 0.1)',
                              borderRadius: '4px',
                            }}
                          >
                            + assign
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </>,
        document.body
      )}
    </div>
  );
}
